import config from "config";
import {
  AuthState,
  CoreStorageManagerInterface,
  isAuthApiError,
  isOAuthError,
  OktaAuthCoreInterface,
  OktaAuthCoreOptions,
  PKCETransactionMeta,
  TransactionManagerInterface,
} from "@okta/okta-auth-js";

import * as heap from "services/heap";

const { analytics } = config.get();

type OktaAuthCore = OktaAuthCoreInterface<
  PKCETransactionMeta,
  CoreStorageManagerInterface<PKCETransactionMeta>,
  OktaAuthCoreOptions,
  TransactionManagerInterface
>;

const identifyInHeap = (email: string) => {
  const emailDomain = email.split("@").pop();

  try {
    heap
      .init()
      .then(() => {
        window.heap.identify(email);
        window.heap.addUserProperties({ email, emailDomain });
      })
      .catch((err) => {
        console.error("failed to init heap", err);
      });
  } catch (error) {
    console.error("heap not registered", error);
  }
};

const isTokenRejectedByOkta = (error: unknown) =>
  (isOAuthError(error) && error.errorCode === "invalid_token") ||
  (isAuthApiError(error) && error.xhr?.status === 401);

const getUserInfo = async (oktaAuth: OktaAuthCore) => {
  try {
    return await oktaAuth?.token?.getUserInfo();
  } catch (error) {
    // Okta is the only thing that notices a revoked token — our API validates
    // the JWT offline and keeps accepting it until it expires. Answer "signed
    // out" so SecureComponent starts a fresh login; throwing instead would land
    // in authState.error, which renders as a blank page. Other failures still
    // throw, so a token Okta cannot vouch for never passes as authenticated.
    if (isTokenRejectedByOkta(error)) {
      return undefined;
    }

    throw error;
  }
};

export const transformAuthState = async (
  oktaAuth: OktaAuthCore,
  authState: AuthState
): Promise<AuthState> => {
  if (!authState.isAuthenticated) {
    return authState;
  }

  const user = await getUserInfo(oktaAuth);
  authState.isAuthenticated = !!user; // convert to boolean

  // still not authenticated or doesn't have an email
  if (!authState.isAuthenticated || !user?.email) {
    return authState;
  }

  // only try to set-up Heap analytics if relevant config is present
  if (analytics.heap) {
    identifyInHeap(user.email.toLowerCase());
  }

  return authState;
};
